import React, {
  createContext, useState, useCallback, useEffect, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { ashTheme } from '@glu/theming';
import { useValidation } from '@glu/validation-react';
import { omit } from 'lodash';
import deepmerge from 'deepmerge';
import flatten, { unflatten } from 'flat';

const BrandingContext = createContext({});

export const BrandingProvider = ({
  children,
  theme,
  baseTheme,
  omitFromUploadMerge,
  accessibilityStandard,
  useFlatStructure,
  ...rest
}) => {
  const composedThemeData = useMemo(() => {
    const composedTheme = baseTheme(theme);
    return useFlatStructure ? flatten(composedTheme) : composedTheme;
  }, [baseTheme, theme, useFlatStructure]);

  const [initialData, setInitialData] = useState(composedThemeData);
  const [FormProvider, formValue, { data, resetData }] = useValidation(
    { ...rest, initialData }
  );

  useEffect(() => {
    setInitialData(composedThemeData);
  }, [composedThemeData]);

  useEffect(() => {
    resetData();
  }, [initialData, resetData]);

  const setBrandingFromUpload = useCallback((uploadedThemeRaw) => {
    const uploadedTheme = omit(uploadedThemeRaw, omitFromUploadMerge);
    const newTheme = deepmerge(useFlatStructure ? unflatten(data) : data, uploadedTheme);

    setInitialData(useFlatStructure ? flatten(newTheme) : newTheme);
  }, [data, useFlatStructure, omitFromUploadMerge]);

  const valueMemo = useMemo(() => ({
    branding: useFlatStructure ? unflatten(data) : data,
    setBrandingFromUpload,
    accessibilityStandard
  }), [accessibilityStandard, data, setBrandingFromUpload, useFlatStructure]);

  return (
    <FormProvider value={formValue}>
      <BrandingContext.Provider
        value={valueMemo}
      >
        {children}
      </BrandingContext.Provider>
    </FormProvider>
  );
};

BrandingProvider.propTypes = {
  children: PropTypes.node.isRequired,
  theme: PropTypes.shape({}),
  baseTheme: PropTypes.func,
  omitFromUploadMerge: PropTypes.arrayOf(PropTypes.string),
  useFlatStructure: PropTypes.bool,
  accessibilityStandard: PropTypes.oneOf(['aa', 'aaa'])
};

BrandingProvider.defaultProps = {
  theme: {},
  baseTheme: ashTheme,
  omitFromUploadMerge: [],
  useFlatStructure: false,
  accessibilityStandard: 'aa'
};

export default BrandingContext;
